import React, { createContext, useContext, useEffect, useState } from 'react';
import { Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

import iconCortPng from 'assets/images/home.png';
import Step2 from './Step2/Step2';
import { useCreateResponseTask } from 'services/task/mutations';
import { Entity, Loan, Task, TaskResponse, TaskState } from 'gql/graphql';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import ModalTask from '../components/Organisms/Modals/ModalTask/ModalTask';
import StateTag from '../components/Atoms/Tags/StateTag/StateTag';
import DropdownBlue from '../../Dropdowns/Dropdown/Dropdown';
import { LoanContext } from 'scenes/Private/LoanDetails/LoanDetails';
import { OpenTaskContext } from 'scenes/Private/LoanList/LoanList';

export const TaskContext = createContext<Task | {}>({});
export const EntityContext = createContext<Entity | {}>({});

interface Props {
  task: Task;
  visible?: boolean;
  handlerClose?: Function;
}

const Payoff = ({ task, visible, handlerClose }: Props) => {
  const loan: Loan | any = useContext(LoanContext);
  const { taskKey, setTaskKey }: any = useContext(OpenTaskContext);

  const { execute: createResponseTask } = useCreateResponseTask();
  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } }
  });

  const [isModalTaskVisible, setModalTaskVisible] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [step, setStep] = useState(1);
  const [responseStep1, setResponseStep1] = useState<TaskResponse | any>();
  const [responseStep2, setResponseStep2] = useState<TaskResponse>();
  const [entity, setEntity] = useState<Entity | any>();

  useEffect(() => {
    if(!isModalTaskVisible && handlerClose)
      handlerClose();
  }, [isModalTaskVisible])

  useEffect(() => {
    setModalTaskVisible(!!visible);
  }, [visible])

  useEffect(() => {
    if (taskKey === 'payoff') {
      setModalTaskVisible(true);
    } else if(!!taskKey)
      setModalTaskVisible(false);
  }, [taskKey]);

  useEffect(() => {
    if (isModalTaskVisible)
      getTaskResponses({ requestPolicy: 'network-only' });
  }, [isModalTaskVisible, step])

  useEffect(() => {
    const step1: any = taskResponses?.find((item) => item.step === 1);
    if (step1) setStep(2);
    setResponseStep1(step1);

    const step2: any = taskResponses?.find((item) => item.step === 2);
    setResponseStep2(step2);
    setIsReview(step2?.state === TaskState.Review);
  }, [taskResponses]);

  const onHelpDocs = () => {
    console.log('onHelpDocs');
  };

  const onChangeStep1 = async (optionKey: "letter" | "contact") => {
    const { createdAt, __typename, task, ...payload } = responseStep1;

    await createResponseTask({
      ...payload,
      metadata: {},
      task: task._id,
      key: optionKey === 'letter' ? 'yes' : 'no',
      label: optionKey === 'letter' ?
        'Yes, I have a payoff letter' : 'No, I have a payoff contact',
    });

    getTaskResponses({ requestPolicy: 'network-only' });
  };

  return (
    <TaskContext.Provider value={task}>
      <EntityContext.Provider value={entity}>
        <ModalTask
          visible={isModalTaskVisible}
          handlerSetVisibile={setModalTaskVisible}
          dotsReview={[responseStep2?.state === TaskState.Review]}
          header={{
            title: <span>UNDERWRITING | MORTGAGE PAYMENTS</span>,
            actionLeft: (
              isReview ? <StateTag state={TaskState.Review} size="large" /> : <img src={iconCortPng} alt="icon" className="paperPng" />
            ),
            actionRight: (
              <Button
                className="button button-transparent"
                onClick={() => onHelpDocs()}
              >
                <QuestionCircleOutlined />
                Help docs
              </Button>
            )
          }}
          animationDirection="onRight"
          dots={true}
          stepsTotal={1}
          currentStep={1}
          setStep={setStep}
          footer={
            <div className="payoff-footer text-footer mt-20">
              <p className="placeholder">
                My payoff {responseStep1?.key === 'yes' ? 'is ' : ' '}
                {step === 2 && responseStep2 &&
                  <b
                    className={`bold ${responseStep2.state === TaskState.Review ? 'is-review' : ''}`}
                    style={{ padding: 0 }}
                  >
                    {responseStep1?.key === 'yes' ? 'letter is not submitted' : 'contact is not submitted'}
                  </b>
                }
                {step === 2 && !responseStep2 &&
                  <DropdownBlue
                    label={responseStep1?.key === 'yes' ? 'letter is not submitted' : 'contact is not submitted'}
                    onClickSelect={onChangeStep1}
                    items={responseStep1?.key !== 'yes' ?
                      [{ key: 'letter', label: 'Letter is not submitted' }] :
                      [{ key: 'contact', label: 'Contact is not submitted' }]
                    }
                    className={`dropdown-blue-bold`}
                  />
                }
              </p>
            </div>
          }
        >
          <section className="children payoff">
            <Step2
              closeTask={() => setModalTaskVisible(false)}
            />
          </section>
        </ModalTask>
      </EntityContext.Provider>
    </TaskContext.Provider>
  );
};
export default Payoff;
